import React, { useState, useEffect, useContext } from "react";
import {
  ShoppingCart,
  Receipt,
  Camera,
  User,
  CreditCard,
  MapPin,
  Plus,
  X,
  Loader2,
  CheckCircle,
  Search,
  Users,
  AlertCircle,
  Trash2
} from "lucide-react";
import api from "../api/axiosInstance";
import { AuthContext } from "../App";
import { triggerSafeDelete } from "./deleteInterceptor";

const MaterialPurchaseTracker = () => {
  const [purchases, setPurchases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useContext(AuthContext);
  console.log(user);
  // Auth context states pulled from session storage parameters
  const [currentUser, setCurrentUser] = useState({
    id: user?.id || '',
    role: user?.role || 'Staff' // e.g., Owner, Admin, Staff
  });

  // Supervisor wallets list container for Admin allocation view
  const [walletUsers, setWalletUsers] = useState([]);

  const [formData, setFormData] = useState({
    project_id: "",
    biller_name: "",
    amount: "",
    purchase_date: new Date().toISOString().split("T")[0],
    paid_by: "",
    payment_method: "Petty Cash",
    wallet_user_id: "", // Connects buy explicitly to an active supervisor pool
    items_summary: "",
    bill_screenshot: null,
  });

  useEffect(() => {
    loadData();

  }, []);

  useEffect(() => {
    if (showModal) {
      if (currentUser.role !== "Owner" && currentUser.role !== "Admin" && currentUser.role !== "owner" && currentUser.role !== "admin") {
        setFormData((prev) => ({ ...prev, wallet_user_id: currentUser.id }));
      } else {
        fetchWalletHoldingUsers();
      }
    }
  }, [showModal]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [purRes, projRes] = await Promise.all([
        api.get("get_material_purchases.php"),
        api.get("get_projects.php"),
      ]);
      setPurchases(purRes.data.purchases || []);
      setProjects(projRes.data.projects || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletHoldingUsers = async () => {
    try {
      const res = await api.get("get_wallet_intelligence.php");
      console.log(res)
      setWalletUsers(res.data.supervisors || []);
    } catch (err) {
      console.error("Failed to fetch wallet holdings:", err);
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, bill_screenshot: e.target.files[0] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.payment_method === "Petty Cash" && !formData.wallet_user_id) {
      alert("Please designate a source wallet account to debit for Petty Cash.");
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));

    try {
      const res = await api.post("save_material_purchase.php", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status === "success") {
        setShowModal(false);
        setFormData({
          project_id: "",
          biller_name: "",
          amount: "",
          purchase_date: new Date().toISOString().split("T")[0],
          paid_by: "",
          payment_method: "Petty Cash",
          wallet_user_id: currentUser.role !== "Owner" && currentUser.role !== "Admin" && currentUser.role !== "owner" && currentUser.role !== "admin" ? currentUser.id : "",
          items_summary: "",
          bill_screenshot: null,
        });
        loadData();
      }
    } catch (err) {
      alert(err.response?.data?.message || "Upload and calculation sequence failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse uppercase tracking-widest text-slate-400">
        Loading Procurement Logs...
      </div>
    );

  return (
    <div className="space-y-8 p-4">
      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
            Site Procurement
          </h1>
          <p className="text-slate-500 font-medium italic text-sm">
            Direct Material Buys & Vendor Payments
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2"
        >
          <ShoppingCart size={18} /> Add Purchase
        </button>
      </div>

      {/* Purchase List */}
      <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <tr>
              <th className="p-8">Date / Shop</th>
              <th className="p-8">Site / Items</th>
              <th className="p-8">Payment Info</th>
              <th className="p-8 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 font-bold">
            {purchases.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50/30">
                <td className="p-8">
                  <p className="text-xs text-slate-400 mb-1">
                    {p.purchase_date}
                  </p>
                  <p className="text-slate-900 uppercase italic tracking-tight">
                    {p.biller_name}
                  </p>
                </td>
                <td className="p-8">
                  <p className="text-indigo-600 text-xs uppercase mb-1">
                    {p.project_name}
                  </p>
                  <p className="text-[10px] text-slate-400 font-medium truncate w-48">
                    {p.items_summary}
                  </p>
                </td>
                <td className="p-8">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded text-[9px] uppercase font-black ${p.payment_method === "Petty Cash"
                        ? "bg-amber-50 text-amber-600 border border-amber-100"
                        : "bg-blue-50 text-blue-600 border border-blue-100"
                        }`}
                    >
                      {p.payment_method}
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase">
                      by {p.paid_by}
                    </span>
                  </div>
                </td>
                <td className="p-8 text-right text-lg font-black italic text-slate-900">
                  ₹{parseFloat(p.amount).toLocaleString()}
                </td>
                <td className="text-right">
                  <button
                    onClick={() => triggerSafeDelete(
                      "material_purchases",
                      p.id,
                      `Material bought from ${p.biller_name} for ₹${p.amount} on ${p.purchase_date}`,
                      () => loadData() // Refreshes your purchase list table
                    )}
                    className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: New Purchase */}
      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
          <form
            onSubmit={handleSubmit}
            className="bg-white w-full max-w-2xl rounded-[48px] shadow-2xl p-12 space-y-6 overflow-y-auto max-h-[90vh]"
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-3xl font-black italic tracking-tighter uppercase text-slate-900">
                  Material Purchase
                </h2>
                <p className="text-[9px] font-black uppercase tracking-widest text-indigo-500 mt-0.5">Asset Registration Slip</p>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="p-2 bg-slate-100 rounded-full text-slate-400 hover:bg-slate-900 hover:text-white transition-all"
              >
                <X />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Shop/Biller Name
                </label>
                <input
                  required
                  className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-bold outline-none transition-all"
                  placeholder="E.g. National Hardware"
                  value={formData.biller_name}
                  onChange={(e) =>
                    setFormData({ ...formData, biller_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Select Site
                </label>
                <select
                  required
                  className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-bold outline-none"
                  value={formData.project_id}
                  onChange={(e) =>
                    setFormData({ ...formData, project_id: e.target.value })
                  }
                >
                  <option value="">Choose Site...</option>
                  {projects.map((pj) => (
                    <option key={pj.id} value={pj.id}>
                      {pj.project_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Amount Paid
                </label>
                <input
                  required
                  type="number"
                  className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-black text-xl outline-none"
                  placeholder="₹ 0.00"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Date
                </label>
                <input
                  type="date"
                  className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-bold outline-none"
                  value={formData.purchase_date}
                  onChange={(e) =>
                    setFormData({ ...formData, purchase_date: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Who Paid?
                </label>
                <input
                  required
                  className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-bold outline-none"
                  placeholder="Supervisor Name / Admin"
                  value={formData.paid_by}
                  onChange={(e) =>
                    setFormData({ ...formData, paid_by: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Method
                </label>
                <select
                  className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-bold outline-none"
                  value={formData.payment_method}
                  onChange={(e) =>
                    setFormData({ ...formData, payment_method: e.target.value })
                  }
                >
                  <option value="Petty Cash">Petty Cash (From Wallet)</option>
                  <option value="UPI/Online">UPI/GPay (Direct Corporate)</option>
                  <option value="Bank Transfer">Bank Transfer (Direct Corporate)</option>
                  <option value="Cash (Admin)">Cash (Office Vault)</option>
                </select>
              </div>
            </div>

            {/* WALLET DEBIT SELECTOR BLOCK */}
            {formData.payment_method === "Petty Cash" && (
              <div className="animate-in fade-in duration-200">
                {currentUser.role === "Owner" || currentUser.role === "Admin" || currentUser.role === "owner" || currentUser.role === "admin" ? (
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 flex items-center gap-1">
                      <Users size={12} /> Debit Target Cash Wallet
                    </label>
                    <select
                      required={formData.payment_method === "Petty Cash"}
                      className="w-full p-4 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-wider outline-none"
                      value={formData.wallet_user_id}
                      onChange={(e) =>
                        setFormData({ ...formData, wallet_user_id: e.target.value })
                      }
                    >
                      <option value="" className="text-slate-400">Select Supervisor Wallet...</option>
                      {walletUsers.map((user) => (
                        <option key={user.id} value={user.id} className="text-white bg-slate-900">
                          {user.name} (Bal: ₹{parseFloat(user.balance).toFixed(0)})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-center gap-2">
                    <AlertCircle size={16} className="text-amber-600 shrink-0" />
                    <p className="text-[10px] font-bold text-amber-700 uppercase tracking-tight">
                      This purchase value will be automatically deducted from your active field wallet.
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                Items Bought
              </label>
              <textarea
                className="w-full p-4 bg-slate-50 border border-transparent focus:border-indigo-500 rounded-2xl font-medium text-sm h-20 outline-none resize-none"
                placeholder="E.g. 5kg Fevicol, 2 Boxes of screws..."
                value={formData.items_summary}
                onChange={(e) =>
                  setFormData({ ...formData, items_summary: e.target.value })
                }
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                Bill Screenshot
              </label>
              <div className="relative w-full p-8 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-all cursor-pointer">
                <Camera className="text-slate-400 mb-2" size={32} />
                <p className="text-[10px] font-black text-slate-400 uppercase">
                  Click to upload bill
                </p>
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleFileChange}
                />
                {formData.bill_screenshot && (
                  <p className="mt-2 text-indigo-600 font-bold text-xs">
                    File: {formData.bill_screenshot.name}
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-6 bg-slate-900 disabled:bg-slate-300 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all uppercase tracking-wider"
            >
              {submitting ? "Processing Registry..." : "Register Purchase"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default MaterialPurchaseTracker;